/**
 * `GET /agent-runs/{id}` returns 404 both for a genuinely unknown run id and
 * for a run the viewer may not see: one that is neither theirs nor in a
 * project their own GitLab credential reads at Reporter or above, or whose
 * visibility check failed (backend `run_visibility.can_view_run`).
 * `POST /agent-runs/{id}/stop` returns the same 404 for any run the viewer
 * doesn't own, unless they are an admin (`_require_owned_run`). Both are
 * identical by design, so a caller cannot probe which run ids exist.
 * `isNotFoundError` is the one place the frontend recognizes that response;
 * callers must render the same "not found" copy either way and must not add
 * text implying the run exists but is merely hidden.
 */

import { isAxiosError } from 'axios';
import { z } from 'zod';
import { LintErrorDetailSchema } from '../schemas/projectPanel.schemas';
import type { LintFinding } from '../types/projectPanel.types';

export function isNotFoundError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 404;
}

/**
 * Save's (and Withdraw's) refusals, for the editor to show as-is: a 409
 * (another MR, a lost race, a stale edit), a 503 (try again), a 403, or a
 * 422 that is either GitLab refusing the commit (a string `detail`) or the
 * lint (`{message, findings}`). Null for anything else, FastAPI's own
 * validation 422 (a list `detail`) included.
 */
export interface SaveErrorDetail {
  status: number;
  message: string;
  findings: LintFinding[];
}

export function saveErrorDetail(error: unknown): SaveErrorDetail | null {
  if (!isAxiosError(error) || error.response === undefined) return null;
  const { status } = error.response;
  const data: unknown = error.response.data;
  if (!data || typeof data !== 'object' || !('detail' in data)) return null;
  const detail: unknown = data.detail;
  if (typeof detail === 'string') return { status, message: detail, findings: [] };
  const lint = LintErrorDetailSchema.safeParse(detail);
  return lint.success ? { status, ...lint.data } : null;
}

const ValidationItemSchema = z.object({
  loc: z.array(z.union([z.string(), z.number()])),
  msg: z.string(),
});

/**
 * Why render refused the form's definition: FastAPI's validation 422 names
 * the first bad field (`on.job_failed: List should have at least 1 item`),
 * without the `body.definition` prefix. Any other failure reads as
 * `panelErrorMessage`.
 */
export function renderErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response?.status === 422) {
    const data: unknown = error.response.data;
    const detail: unknown = data && typeof data === 'object' && 'detail' in data ? data.detail : null;
    const first = ValidationItemSchema.safeParse(Array.isArray(detail) ? detail[0] : null);
    if (first.success) {
      const field = first.data.loc.slice(2).join('.');
      return field === '' ? first.data.msg : `${field}: ${first.data.msg}`;
    }
  }
  return panelErrorMessage(error);
}

/**
 * What to show for a failed panel request: the backend's own `detail`
 * (its copy, e.g. "no such project" or "try again") when it sent one,
 * else the error's message.
 */
export function panelErrorMessage(error: unknown): string {
  const detail = saveErrorDetail(error);
  if (detail !== null) return detail.message;
  return error instanceof Error ? error.message : 'Request failed';
}
